import React, { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import {
  Alert,
  AlertColor,
  AppBar,
  Box,
  Collapse,
  Container,
  CssBaseline,
  Stack
} from '@mui/material'

import GlobalStyle from '../components/GlobalStyle'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../redux'
import { createNotification } from '../redux/slices/notifications'
import NavbarLanguagesDropdown from '../components/navbar/NavbarLanguagesDropdown'
import ThemeButton from '../components/ThemeButton'
import InstructionsButton from '../components/NewUserInstructionsButton'
import Brand from '../components/Brand'
import Footer from '../components/auth/Footer'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: 100%;
`

const Auth: React.FC = ({ children }) => {
  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The notification state.
   */
  const { notification } = useSelector((state: RootState) => state.notifications)

  /**
   * The location.
   */
  const location = useLocation()

  /**
   * The alert severity.
   */
  const severity = notification?.type as AlertColor

  /**
   * Indicate if the brand should be shown.
   */
  const showBrand =
    location.pathname !== '/auth/terms-of-service' && location.pathname !== '/auth/privacy-policy'

  const clearNotifications = () => {
    dispatch(
      createNotification({
        show: false,
        type: '',
        message: '',
        timeout: 0
      })
    )
  }

  const handleClose = () => {
    clearNotifications()
  }

  useEffect(() => {
    if (notification?.type == 'success' || notification?.type == 'warning') {
      const timeout = setTimeout(() => {
        clearNotifications()
      }, notification.timeout)
      return () => clearTimeout(timeout)
    }
  }, [notification?.show])

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <AppBar color="transparent" position="static" sx={{ boxShadow: 'none' }}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1} sx={{ p: 2 }}>
          <NavbarLanguagesDropdown />
          <ThemeButton size="large" />
          <InstructionsButton size="large" />
        </Stack>
      </AppBar>

      <Container
        maxWidth="sm"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: showBrand ? 'center' : 'flex-start',
          alignItems: showBrand ? 'center' : 'flex-start',
          flexGrow: 1,
          height: '100%'
        }}
      >
        <Box
          style={{
            position: 'fixed',
            top: '80px',
            right: '16px',
            zIndex: 9999
          }}
        >
          <Collapse in={notification?.show}>
            <Alert variant="filled" severity={severity} onClose={handleClose}>
              {notification?.message}
            </Alert>
          </Collapse>
        </Box>

        {showBrand && (
          <Box sx={{ mb: 6 }}>
            <Brand />
          </Box>
        )}

        {children}
        <Outlet />
      </Container>
      <Footer />
    </Root>
  )
}

export default Auth
